import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader } from "@/components/host/hostUi";

const currencySymbol = (c) => ({ USD: "$", EUR: "€", GBP: "£" }[(c || "USD").toUpperCase()] || "$");

const formatMonth = (s) => {
  if (!s) return "";
  const [y, m] = s.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "short", year: "numeric" });
};

const HostRevenuePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(6);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/revenue?months=${months}`, { headers });
      if (!res.ok) {
        toast.error("Couldn't load revenue");
        return;
      }
      setData(await res.json());
    } catch (_) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Find max total across all month rows to scale bars
  const maxRow = data?.by_month?.reduce((m, r) => Math.max(m, r.total || 0), 0) || 1;

  return (
    <HostLayout>
      <div data-testid="host-revenue-page">
        <HostPageHeader
          icon={TrendingUp}
          title="Revenue"
          subtitle="Money earned across every game, series and trip you organise."
          actions={
            <Select value={String(months)} onValueChange={(v) => setMonths(parseInt(v, 10))}>
              <SelectTrigger className="w-40" data-testid="revenue-window-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Last 3 months</SelectItem>
                <SelectItem value="6">Last 6 months</SelectItem>
                <SelectItem value="12">Last 12 months</SelectItem>
                <SelectItem value="24">Last 24 months</SelectItem>
              </SelectContent>
            </Select>
          }
        />

        {loading && (
          <div className="py-12 flex items-center justify-center text-muted-foreground gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && data && (
          <>
            {/* Lifetime totals per currency */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="revenue-lifetime-cards">
              {Object.keys(data.lifetime_total_by_currency || {}).length === 0 ? (
                <Card className="p-6 sm:col-span-3 text-center text-muted-foreground" data-testid="revenue-empty">
                  No revenue yet. Once travellers book your events, you&apos;ll see the breakdown here.
                </Card>
              ) : Object.entries(data.lifetime_total_by_currency).map(([cur, total]) => {
                const bd = data.lifetime_breakdown?.[cur] || {};
                return (
                  <Card key={cur} className="p-4" data-testid={`lifetime-card-${cur}`}>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Lifetime · {cur}</p>
                    <p className="font-display text-3xl">{currencySymbol(cur)}{total.toLocaleString()}</p>
                    <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                      <div>
                        <p className="text-muted-foreground">Games</p>
                        <p className="font-medium">{currencySymbol(cur)}{(bd.games || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Series</p>
                        <p className="font-medium">{currencySymbol(cur)}{(bd.series || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Trips</p>
                        <p className="font-medium">{currencySymbol(cur)}{(bd.trips || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Window totals */}
            {Object.keys(data.totals_by_currency || {}).length > 0 && (
              <Card className="p-4 mb-5" data-testid="revenue-window-totals">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Last {data.window_months} months
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                  {Object.entries(data.totals_by_currency).map(([cur, total]) => (
                    <div key={cur}>
                      <span className="font-display text-xl">{currencySymbol(cur)}{total.toLocaleString()}</span>
                      <span className="text-muted-foreground text-sm ml-1">{cur}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Month-by-month breakdown */}
            <Card className="p-4" data-testid="revenue-monthly-list">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg">Monthly breakdown</h2>
              </div>
              {data.by_month.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No revenue in this window.</p>
              ) : (
                <div className="divide-y divide-border">
                  {data.by_month.map((row, i) => (
                    <div key={`${row.month}-${row.currency}-${i}`} className="py-3" data-testid={`revenue-row-${row.month}-${row.currency}`}>
                      <div className="flex items-baseline justify-between gap-3 mb-1.5">
                        <p className="text-sm font-medium">
                          {formatMonth(row.month)} <span className="text-muted-foreground text-xs ml-1">{row.currency}</span>
                        </p>
                        <p className="font-display text-base">{currencySymbol(row.currency)}{row.total.toLocaleString()}</p>
                      </div>
                      {/* Stacked bar */}
                      <div className="h-2 rounded-full bg-muted overflow-hidden flex">
                        {["games", "series", "trips"].map((kind) => {
                          const pct = maxRow > 0 ? ((row[kind] || 0) / maxRow) * 100 : 0;
                          if (pct === 0) return null;
                          const colour = kind === "games" ? "bg-primary" : kind === "series" ? "bg-emerald-500" : "bg-fuchsia-500";
                          return <div key={kind} className={`${colour} h-full`} style={{ width: `${pct}%` }} />;
                        })}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mt-1.5">
                        {row.games > 0 && (
                          <span><span className="inline-block w-2 h-2 rounded-full bg-primary mr-1" />Games {currencySymbol(row.currency)}{row.games.toLocaleString()}</span>
                        )}
                        {row.series > 0 && (
                          <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />Series {currencySymbol(row.currency)}{row.series.toLocaleString()}</span>
                        )}
                        {row.trips > 0 && (
                          <span><span className="inline-block w-2 h-2 rounded-full bg-fuchsia-500 mr-1" />Trips {currencySymbol(row.currency)}{row.trips.toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </HostLayout>
  );
};

export default HostRevenuePage;
